import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCheckoutCart } from '../contexts/CheckoutCartContext';
import { pushEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import { isPlanCheckoutPath, navigateToCheckoutOverlay } from '../utils/checkoutOverlayNavigation';
import useUpgradeOffer from '../hooks/useUpgradeOffer';
import { UpgradeOfferState } from './UpgradeOfferSummary';
import './PlanChooser.css';

const SALES_PAGE_EXPERIMENT_PARAM_KEYS = [
  '$feature/sales_page_signup_framing_v4_variant',
  '$feature_flag_sales_page_signup_framing_v4_variant',
  '$feature/sales_page_signup_framing_v5_variant',
  '$feature_flag_sales_page_signup_framing_v5_variant',
  '$feature/google_search_signup_experience_v1_variant',
  '$feature_flag_google_search_signup_experience_v1_variant',
  '$feature/google_search_signup_experience_v2_variant',
  '$feature_flag_google_search_signup_experience_v2_variant',
  '$feature/google_search_auth_first_personalization_2026_08_variant',
  '$feature_flag_google_search_auth_first_personalization_2026_08_variant',
  'feature_flag',
  'experiment_name',
  'experiment_variant',
  'sales_page_signup_framing_v4_experiment_name',
  'sales_page_signup_framing_v5_experiment_name',
  'sales_page_signup_framing_variant',
  'sales_page_signup_framing_v4_variant',
  'sales_page_signup_framing_v4_route_variant',
  'sales_page_signup_framing_v5_variant',
  'sales_page_signup_framing_v5_route_variant',
  'google_search_signup_experience_v1_experiment_name',
  'google_search_signup_experience_v1_variant',
  'google_search_signup_experience_v2_experiment_name',
  'google_search_signup_experience_v2_variant',
  'google_search_auth_first_personalization_2026_08_experiment_name',
  'google_search_auth_first_personalization_2026_08_variant',
  'google_search_lp_job',
  'sales_page_form_variant',
  'sales_page_framing_variant',
  'sales_page_design_variant',
];

/**
 * PlanChooser - Full-page paywall blocker shown when users lack payment
 *
 * Used by ProtectedRoute when requirePayment=true and user is not paying.
 * This is a BLOCKING component (not a modal) - user cannot dismiss it.
 * They must click "Upgrade to Starter Plan" to proceed.
 *
 * Matches PaywallModal visual design but designed for route-level blocking.
 */
export default function PlanChooser({
  heading = 'Upgrade Required',
  body = 'This feature is not included in your current plan. Upgrade to unlock full access.',
  checkoutUrl = null,
  useOffer = useUpgradeOffer,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { replaceCart, pending: cartPending } = useCheckoutCart();
  const [error, setError] = useState(null);
  const { offer, loading: offerLoading, error: offerError } = useOffer(true);
  // Node's offer is the only source of the destination and price (Rob,
  // 2026-10-14). There is no "View plans" fallback: without an offer the
  // button stays disabled.
  const resolvedCheckoutUrl = offer?.checkoutUrl || offer?.contactSalesUrl || checkoutUrl;
  const successUrl = useMemo(
    () => searchParams.get('successUrl') || `${location.pathname}${location.search}`,
    [location.pathname, location.search, searchParams],
  );
  const salesExperimentParams = useMemo(() => (
    SALES_PAGE_EXPERIMENT_PARAM_KEYS.reduce((params, key) => {
      const value = searchParams.get(key);
      if (value) {
        params.set(key, value);
      }
      return params;
    }, new URLSearchParams())
  ), [searchParams]);

  const buildCheckoutPath = () => {
    const destination = new URL(resolvedCheckoutUrl, 'https://botspot.trade');
    const params = new URLSearchParams(destination.search);
    for (const [key, value] of salesExperimentParams.entries()) {
      if (!params.has(key)) params.set(key, value);
    }
    params.set('successUrl', successUrl);
    return `${destination.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
  };

  const handleUpgradeClick = async () => {
    setError(null);

    // Track CTA click
    pushEvent({
      event: ANALYTICS_EVENTS.AI_AGENT_PAYWALL_CTA_CLICKED?.name || 'paywall_cta_clicked',
      cta_type: offer?.terminal ? 'contact_sales' : 'upgrade_plan',
      source: 'plan_chooser',
      checkoutUrl: resolvedCheckoutUrl,
    });

    if (!resolvedCheckoutUrl) return;
    try {
      // Plan pages (/checkout/light-plan, /checkout/starter-plan) price the
      // plan themselves and run both checkout tests; only the cart needs filling.
      const opensPlanPage = isPlanCheckoutPath(
        new URL(resolvedCheckoutUrl, 'https://botspot.trade').pathname,
      );
      if (!opensPlanPage && offer?.productSlug && offer?.plans?.length) {
        const selectedPlan =
          offer.plans.find((plan) => plan.checkoutUrl === offer.checkoutUrl) || offer.plans[0];
        await replaceCart(
          [
            {
              productSlug: offer.productSlug,
              planKey: selectedPlan.planKey,
              quantity: 1,
            },
          ],
          { flowContext: 'plan_upgrade' },
        );
      }
      navigateToCheckoutOverlay(navigate, buildCheckoutPath(), location);
    } catch (err) {
      console.error('[PlanChooser] Failed to prefill checkout cart', err);
      setError('We could not prefill that plan. Opening checkout, please select a plan there.');
      navigateToCheckoutOverlay(navigate, buildCheckoutPath(), location);
    }
  };

  return (
    <div className="plan-chooser-blocker">
      <div className="plan-chooser-content">
        {/* Bootstrap's visually-hidden keeps the route heading for screen readers; the offer card carries the visible title. */}
        <h2 className="visually-hidden">{heading}</h2>
        <UpgradeOfferState
          offer={offer}
          loading={offerLoading}
          error={offerError}
          reason={body}
          onUpgrade={handleUpgradeClick}
          pending={cartPending || offerLoading}
        />
        {(error || offerError) && (
          <div className="plan-chooser-error">{error || offerError}</div>
        )}
      </div>
    </div>
  );
}
